import { RefreshCw } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'react-toastify';

import { routes } from '~/app/routes';
import { TextField } from '~/components/form';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ConfirmModal,
  FormActions,
  SkeletonFields,
  LoadError,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import {
  createDefaultTokenLimitsFormState,
  hasTokenLimitsChanges,
  tokenLimitsFormFromLimits,
  tokenLimitsFormToSettings,
  tokenLimitsSchema,
  type TokenLimitsFormState,
} from '~/lib/ai/settingsForm';
import { useGetTokenLimitsQuery, useUpdateTokenLimitsMutation } from '~/modules/query/ai/settings';

/**
 * Token budgets: one per-user daily limit and one organization monthly limit.
 * Both must be whole numbers above zero; the service refuses anything else.
 */
const FORM_ID = 'ai-settings-limits-form';
const FIELD_IDS: Record<string, string> = {
  dailyUserLimit: 'ai-settings-daily-user-limit',
  monthlyOrgLimit: 'ai-settings-monthly-org-limit',
};

export default function GeneralSection() {
  const { data, error, isLoading, isFetching, refetch } = useGetTokenLimitsQuery();
  const [updateTokenLimits, { isLoading: isSaving }] = useUpdateTokenLimitsMutation();
  const limits = data?.limits;
  const [form, setForm] = useState<TokenLimitsFormState>(createDefaultTokenLimitsFormState);
  const validation = useFormValidation(
    tokenLimitsSchema,
    form,
    (field) => FIELD_IDS[field] ?? field,
  );

  // The limits are one unversioned record, so the server value is the saved baseline: a refetch
  // (Refresh, or the save landing) reseeds the form, and `dirty` is the form against it.
  useEffect(() => {
    if (limits) {
      setForm(tokenLimitsFormFromLimits(limits));
      validation.reset();
    }
  }, [limits]);

  const dirty = hasTokenLimitsChanges(form, limits);
  const guard = useUnsavedGuard(dirty);
  const updateField = (key: keyof TokenLimitsFormState, value: string) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const save = async (event: FormEvent) => {
    event.preventDefault();

    if (!validation.submit()) {
      return;
    }

    const payload = tokenLimitsFormToSettings(form);

    if (!payload) {
      return;
    }

    try {
      await updateTokenLimits(payload).unwrap();
      toast.success('Token limits updated');
    } catch (saveError) {
      toast.error(
        apiErrorMessage(saveError as ApiErrorInput) ??
          'The token limits could not be saved. Try again.',
      );
    }
  };

  return (
    <Card>
      <CardHeader
        title="Token limits"
        description="Per-user daily and organization monthly token budgets."
        actions={
          <Button
            variant="outline"
            size="sm"
            loading={isFetching}
            startIcon={<RefreshCw className="size-4" aria-hidden="true" />}
            onClick={() => refetch()}
            testId="ai-settings-limits-refresh"
          >
            Refresh
          </Button>
        }
      />
      <CardBody>
        {isLoading ? (
          <SkeletonFields fields={2} />
        ) : error && !data ? (
          <LoadError
            what="the token limits"
            message={apiErrorMessage(error as ApiErrorInput)}
            onRetry={() => refetch()}
            testId="ai-settings-limits"
          />
        ) : (
          <form
            id={FORM_ID}
            noValidate
            onSubmit={save}
            className="grid grid-cols-1 gap-6 lg:grid-cols-2"
          >
            <TextField
              id="ai-settings-daily-user-limit"
              label="Daily user limit"
              type="number"
              min={1}
              step={1}
              value={form.dailyUserLimit}
              onChange={(value) => updateField('dailyUserLimit', value)}
              onBlur={() => validation.touch('dailyUserLimit')}
              error={validation.errors.dailyUserLimit}
              helperText="Tokens a single user may spend per day."
              testId="ai-settings-daily-user-limit"
            />
            <TextField
              id="ai-settings-monthly-org-limit"
              label="Monthly organization limit"
              type="number"
              min={1}
              step={1}
              value={form.monthlyOrgLimit}
              onChange={(value) => updateField('monthlyOrgLimit', value)}
              onBlur={() => validation.touch('monthlyOrgLimit')}
              error={validation.errors.monthlyOrgLimit}
              helperText="Tokens the whole organization may spend per month."
              testId="ai-settings-monthly-org-limit"
            />
          </form>
        )}
      </CardBody>
      {!isLoading && !(error && !data) ? (
        <CardFooter>
          <FormActions
            discardHref={routes.dashboard}
            form={FORM_ID}
            busy={isSaving}
            saveLabel="Save changes"
            disabled={!dirty}
            disabledReason="No changes to save"
            saveTestId="ai-settings-limits-save"
            discardTestId="ai-settings-limits-cancel"
          />
        </CardFooter>
      ) : null}
      <ConfirmModal
        isOpen={guard.blocked}
        title="Discard unsaved changes?"
        message={guard.message}
        confirmLabel="Discard changes"
        testId="unsaved-guard"
        onConfirm={guard.leave}
        onClose={guard.stay}
      />
    </Card>
  );
}
